import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Grid,
} from "@mui/material";
import {
  PlayArrow,
  Quiz,
  Timer,
  EmojiEvents,
  Warning,
  RestartAlt,
  PlayCircleOutline,
} from "@mui/icons-material";
import { getModuleQuestions } from "../../services/moduleQuizService";
import ModuleQuizComponent from "./ModuleQuizComponent";
import {
  quizStorageService,
  QuizState,
} from "../../services/quiz/quizStorageService";

interface QuizStartScreenProps {
  moduleId: string;
  moduleName: string;
  week: number;
  onComplete: (results: any) => void;
  onExit: () => void;
}

interface QuizData {
  questions: any[];
  totalQuestions: number;
  totalPoints: number;
  estimatedTime: number;
}

const QuizStartScreen: React.FC<QuizStartScreenProps> = ({
  moduleId,
  moduleName,
  week,
  onComplete,
  onExit,
}) => {
  const [quizData, setQuizData] = useState<QuizData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quizStarted, setQuizStarted] = useState(false);

  const [hasSavedState, setHasSavedState] = useState(false);
  const [savedProgress, setSavedProgress] = useState<{
    currentQuestion: number;
    totalQuestions: number;
    answeredCount: number;
  } | null>(null);
  const [initialQuizState, setInitialQuizState] = useState<QuizState | null>(
    null
  );

  const quizId = `${moduleName}_week${week}`.replace(/[^a-zA-Z0-9_]/g, "_");

  const hasRealAnswer = (value: any): boolean => {
    // Check for null/undefined/empty string
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      value === "SKIPPED"
    ) {
      return false;
    }

    // Check for empty array
    if (Array.isArray(value) && value.length === 0) {
      return false;
    }

    // Check for object with all empty string values
    if (typeof value === "object" && !Array.isArray(value)) {
      const hasNonEmptyValue = Object.values(value).some(
        (v) => v !== "" && v !== null && v !== undefined
      );
      return hasNonEmptyValue;
    }

    // For arrays with items or other truthy values
    return true;
  };

  useEffect(() => {
    const loadQuizData = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getModuleQuestions(moduleId);

        if (!data || !data.questions || data.questions.length === 0) {
          throw new Error("No questions available for this module");
        }

        setQuizData(data);

        // Check for saved state
        const savedState = quizStorageService.loadQuizState(quizId);

        // 🆕 ADD VALIDATION - only consider it saved if there are actual answers
        if (savedState && savedState.userAnswers.size > 0) {
          let realAnswerCount = 0;
          savedState.userAnswers.forEach((value) => {
            if (hasRealAnswer(value)) {
              realAnswerCount++;
            }
          });

          console.log("📊 Answer analysis", {
            totalEntries: savedState.userAnswers.size,
            realAnswers: realAnswerCount,
            entries: Array.from(savedState.userAnswers.entries()),
          });

          // Only treat as "saved" if at least 1 REAL answer
          if (realAnswerCount > 0) {
            setHasSavedState(true);
            setSavedProgress({
              currentQuestion: savedState.currentQuestionIndex + 1,
              totalQuestions: savedState.totalQuestions,
              answeredCount: realAnswerCount, // ✅ Use real count
            });
            console.log("📥 Found saved quiz state", {
              currentQuestion: savedState.currentQuestionIndex + 1,
              answeredCount: realAnswerCount,
            });
          } else {
            // All answers are empty - clear it
            console.log("🗑️ Clearing state with only empty answers");
            quizStorageService.removeQuizState(quizId);
            setHasSavedState(false);
            setSavedProgress(null);
          }
        } else {
          setHasSavedState(false);
          setSavedProgress(null);
        }
      } catch (err) {
        // ... error handling
      } finally {
        setLoading(false);
      }
    };

    loadQuizData();
  }, [moduleId, quizId]);

  const handleStartQuiz = () => {
    setInitialQuizState(null);
    setQuizStarted(true);
  };

  const handleResumeQuiz = () => {
    const state = quizStorageService.loadQuizState(quizId);
    setInitialQuizState(state); // Pass saved state
    setQuizStarted(true);
    console.log("▶️ Resuming quiz from saved state");
  };

  // 🆕 ADD handleStartFresh
  const handleStartFresh = () => {
    if (
      window.confirm(
        `Are you sure you want to delete your progress?\n\nYou have answered ${savedProgress?.answeredCount} questions.`
      )
    ) {
      quizStorageService.removeQuizState(quizId);
      setHasSavedState(false);
      setSavedProgress(null);
      setInitialQuizState(null);
      setQuizStarted(true);
      console.log("🔄 Starting fresh - cleared saved state");
    }
  };

  const handleQuizComplete = (results: any) => {
    quizStorageService.removeQuizState(quizId);
    console.log("✅ Quiz submitted - cleared saved state for:", quizId);

    // Reset local state
    setHasSavedState(false);
    setSavedProgress(null);
    setInitialQuizState(null);
    setQuizStarted(false);

    onComplete(results);
  };

  const handleQuizExit = () => {
    setQuizStarted(false);
  };

  // Show loading state
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6">Loading Quiz</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Preparing your quiz questions...
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Show error state
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Unable to Load Quiz
            </Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button variant="outlined" onClick={onExit} startIcon={<Warning />}>
              Back to Module
            </Button>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              startIcon={<PlayArrow />}
            >
              Retry
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Show quiz component when started
  if (quizStarted && quizData) {
    return (
      <ModuleQuizComponent
        questions={quizData.questions}
        moduleId={moduleId}
        moduleName={moduleName}
        week={week}
        initialState={initialQuizState}
        onComplete={handleQuizComplete}
        onExit={handleQuizExit}
      />
    );
  }

  // Show quiz start screen
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Quiz Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Quiz sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            {moduleName} Quiz
          </Typography>
          <Chip label={`Week ${week}`} color="primary" sx={{ mb: 2 }} />
        </Box>

        {/*  Saved Progress Alert */}
        {hasSavedState && savedProgress && (
          <Alert severity="warning" icon={<PlayCircleOutline />} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              ⚠️ Previous Progress Found
            </Typography>
            <Typography variant="body2">
              You have answered <strong>{savedProgress.answeredCount}</strong>{" "}
              out of {savedProgress.totalQuestions} questions. You were on
              question <strong>{savedProgress.currentQuestion}</strong>.
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Choose "Resume Quiz" to continue, or "Start Fresh" to delete this
              progress.
            </Typography>
          </Alert>
        )}

        {/* Quiz Information */}
        {quizData && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h6" color="primary">
                  {quizData.totalQuestions}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Questions
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: "center" }}>
                <EmojiEvents
                  sx={{ fontSize: 32, color: "warning.main", mb: 1 }}
                />
                <Typography variant="h6" color="primary">
                  {quizData.totalPoints}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Points
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: "center" }}>
                <Timer sx={{ fontSize: 32, color: "info.main", mb: 1 }} />
                <Typography variant="h6" color="primary">
                  {Math.round(quizData.estimatedTime / 60000)} min
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Estimated Time
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Instructions */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Instructions:</strong>
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 1 }}>
            • Answer all questions to the best of your ability
            <br />
            • You can navigate between questions using the Previous/Next buttons
            <br />
            • Your progress is automatically saved
            <br />• Click Submit when you're ready to complete the quiz
          </Typography>
        </Alert>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button variant="outlined" onClick={onExit} size="large">
            Back to Module
          </Button>

          {/* 🆕 REPLACE with conditional buttons */}
          {hasSavedState ? (
            <>
              <Button
                variant="outlined"
                onClick={handleStartFresh}
                startIcon={<RestartAlt />}
                size="large"
                color="warning"
              >
                Start Fresh
              </Button>
              <Button
                variant="contained"
                onClick={handleResumeQuiz}
                startIcon={<PlayCircleOutline />}
                size="large"
                disabled={!quizData}
              >
                Resume Quiz
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={handleStartQuiz}
              startIcon={<PlayArrow />}
              size="large"
              disabled={!quizData}
            >
              Start Quiz
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default QuizStartScreen;
